/**
 * Human-readable text for an Opengater API failure. Prefers the server's
 * `detail`, falls back to friendly messages for the statuses we care about.
 * Shared across OG screens (config forms, CRUD lists, user card).
 */
import { ApiError } from '../client';
const DETAIL_MESSAGES = {
    service_type_conflict: 'У провайдера уже настроена отдельная касса для бота или сайта.',
    shared_not_supported_for_store_kassa: 'Касса магазина приложений не может использовать общий канал.',
    hidden_commission_not_supported_for_store_kassa: 'Для кассы магазина приложений нельзя задать скрытую комиссию.',
    return_url_not_supported_for_store_kassa: 'Для кассы магазина приложений нельзя задать адрес возврата.',
    payment_system_not_found: 'Касса не найдена. Обновите список и попробуйте снова.',
    payment_product_not_found: 'Товар не найден. Возможно, он уже удалён.',
    bot_image_type_unsupported: 'Нужен статический JPG или PNG.',
    bot_image_empty: 'Файл пустой.',
    bot_image_too_large: 'Файл больше 5 МБ.',
    storage_rejected_upload: 'Хранилище отклонило файл.',
    storage_unavailable: 'Хранилище временно недоступно.',
    landing_card_not_found: 'Карточка не найдена. Обновите список и попробуйте снова.',
    landing_selection_conflict: 'Состав карточки изменился. Обновите данные и повторите.',
    landing_card_no_active_locations: 'Для карточки нужна хотя бы одна доступная локация.',
    landing_selection_invalid: 'Проверьте выбранные локации и опции.',
    landing_device_number_out_of_range: 'Количество устройств вне разрешённого диапазона.',
    user_deletion_failure_not_found: 'Проблема удаления не найдена. Обновите список.',
    user_deletion_failure_resolved: 'Проблема уже закрыта. Данные будут обновлены.',
    user_deletion_failure_not_retryable: 'Переотправить нечего. Проверьте данные вручную и закройте проблему с комментарием.',
    deletion_publication_unavailable: 'Брокер не подтвердил отправку команды. Повторите позже.',
    'Admin or support access required': 'Для этого раздела нужны права администратора или поддержки.',
    'Administrative operation requires elevated access': 'Эта операция доступна только администратору.',
    'Administrative operation not found': 'Операция не найдена.',
    user_not_found: 'Пользователь не найден.',
    deletion_retry_via_broker_only: 'Удаление повторяется автоматически. Для ручного разбора откройте «Проблемные удаления».',
    'Idempotency key belongs to a different administrative request': 'Ключ операции уже использован для другого действия. Создайте новое действие.',
    'User deletion is in progress': 'Пользователь удаляется. Действие недоступно.',
    'User deletion is already active': 'Удаление этого пользователя уже выполняется.',
    'Current balance does not allow this adjustment': 'Текущий баланс не подходит для выбранного обнуления.',
    'Balance is below the daily tariff': 'На балансе недостаточно средств для подписки.',
    'Administrative operation target not found': 'Пользователь не найден.',
    'Invalid administrative operation parameters': 'Проверьте параметры действия.',
};
function detailText(value) {
    const detail = value.trim();
    if (/^User \d+ has no issued subscription to revoke$/.test(detail)) {
        return 'Перевыпускать нечего: у пользователя ещё нет аккаунта в VPN-панели или ссылка не выдана.';
    }
    return DETAIL_MESSAGES[detail] ?? value;
}
export function apiErrorText(e, fallback = 'Не удалось выполнить запрос.') {
    if (e instanceof ApiError) {
        if (e.status === 401)
            return 'Сессия истекла. Войдите заново.';
        if (e.status === 429) {
            return e.retryAfterSeconds != null
                ? `Слишком много запросов. Повторите через ${e.retryAfterSeconds} сек.`
                : 'Слишком много запросов. Следующая проверка будет позже.';
        }
        const detail = e.body?.detail;
        // 422 `detail` is a string for a business rule, or an array of {loc,msg,type}
        // for schema validation — surface both.
        if (typeof detail === 'string')
            return detailText(detail);
        if (Array.isArray(detail)) {
            const msgs = detail
                .map((d) => (d && typeof d === 'object' ? d.msg : null))
                .filter((m) => typeof m === 'string');
            if (msgs.length)
                return msgs.join('; ');
        }
        if (detail && typeof detail === 'object') {
            const objectDetail = detail;
            const code = objectDetail.code;
            if (typeof code === 'string' && DETAIL_MESSAGES[code.trim()])
                return detailText(code);
            for (const key of ['message', 'reason', 'description']) {
                const value = objectDetail[key];
                if (typeof value === 'string' && value.trim())
                    return value;
            }
            if (typeof code === 'string' && code.trim())
                return detailText(code);
        }
        if (e.status === 403)
            return 'Недостаточно прав для этого действия.';
        if (e.status === 502 || e.status === 503)
            return 'Связанный сервис временно недоступен.';
        if (e.status === 0)
            return e.message;
        return `Ошибка ${e.status}`;
    }
    return fallback;
}
